import React, { useEffect, useState } from "react";
import EmptyState from "../Global/EmptyState";
import TenantTableSkeleton from "./TenantTableSkeleton";
import TenantTable from "./TenantTable";
import TenantTableHeader from "./TenantTableHeader";
import {
  allTenants,
  setTenants,
  allAssignments,
  setAssignments,
  activeTenantActions,
} from "../../Features/Tenants/tenantSlice";
import { useAppDispatch, useAppSelector } from "../../hooks";
import {
  getAllTenants,
  getAllAssignments,
  getAllCustomerOverviewSnapshots,
  getAllCustomerRunbookExecutions,
  getAllCustomerLogicAppExecutions,
} from "../../Services/CommandCenterService";
import AddTenantModal from "./AddTenantModal";
import { CustomerOverviewSnapshot } from "../../Models/CustomerOverviewSnapshot";
import { CustomerRunbookExecution } from "../../Models/CustomerRunbookExecution";
import { CustomerLogicAppExecution } from "../../Models/CustomerLogicAppExecution";

const Tenants: React.FC = () => {
  const [loading, setIsLoading] = useState(true);
  const [overviewByTenantId, setOverviewByTenantId] = useState<
    Record<string, CustomerOverviewSnapshot>
  >({});
  const [runbookExecutions, setRunbookExecutions] = useState<
    CustomerRunbookExecution[]
  >([]);
  const [logicAppExecutions, setLogicAppExecutions] = useState<
    CustomerLogicAppExecution[]
  >([]);
  const dispatch = useAppDispatch();
  const tenants = useAppSelector(allTenants);
  const assignments = useAppSelector(allAssignments);
  const actions = useAppSelector(activeTenantActions);

  useEffect(() => {
    const fetchData = async () => {
      const data = await getAllTenants();
      dispatch(setTenants(data));
      setIsLoading(false);

      const [assignmentsResult, snapshotsResult, runbookExecutionsResult, logicAppExecutionsResult] = await Promise.all([
        getAllAssignments(),
        getAllCustomerOverviewSnapshots().catch(
          () => [] as CustomerOverviewSnapshot[]
        ),
        getAllCustomerRunbookExecutions().catch(
          () => [] as CustomerRunbookExecution[]
        ),
        getAllCustomerLogicAppExecutions().catch(
          () => [] as CustomerLogicAppExecution[]
        ),
      ]);

      const nextOverviewByTenantId = snapshotsResult.reduce(
        (acc, snapshot) => {
          if (snapshot?.azureTenantId) {
            acc[snapshot.azureTenantId] = snapshot;
          }
          return acc;
        },
        {} as Record<string, CustomerOverviewSnapshot>
      );

      dispatch(setAssignments(assignmentsResult));
      setOverviewByTenantId(nextOverviewByTenantId);
      setRunbookExecutions(runbookExecutionsResult);
      setLogicAppExecutions(logicAppExecutionsResult);
    };
    fetchData();
  }, [dispatch]);

  if (loading) {
    return (
      <>
        <TenantTableHeader />
        <TenantTableSkeleton />
      </>
    );
  }

  return (
    <>
      <TenantTableHeader />

      {tenants.length ? (
        <TenantTable
          tenants={tenants}
          assignments={assignments}
          overviewByTenantId={overviewByTenantId}
          runbookExecutions={runbookExecutions}
          logicAppExecutions={logicAppExecutions}
        />
      ) : (
        <EmptyState
          heading="You haven't added any tenants."
          text="Let's get started"
        >
          <AddTenantModal>Add your first tenant</AddTenantModal>
        </EmptyState>
      )}
    </>
  );
};

export default Tenants;
